/**
 * Per-game Penelope control hook — the data half of the control on
 * /live/:gameKey. Penelope is the per-trader Pinnacle maker bot
 * (relay/src/services/penelope). A game is shadow by default (decides and
 * simulates, never writes to Kalshi); its baseline goes live only when the box
 * allows it and the trader holds the live grant.
 *
 * One read: GET /api/penelope/games/by-event/:eventTicker returns the caller's
 * own game for this event (or null) and whether one can be attached. Every
 * mutation is a relay desired-state write the conductor enacts within a second;
 * the relay re-checks the grant on each one.
 */
import { useCallback, useMemo, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { eventTickerOfMarket } from '@/lib/vanilla/slate';
import type { GameRow } from '@/views/ValueDashboard/types';
import { larryLegsOf } from '@/views/Cockpit/larryMatch';
import { useVisibilityAwarePoll } from './useVisibilityAwarePoll';

const POLL_MS = 2_000;

export type PenelopeLean = 'neutral' | 'a' | 'b';
export type PenelopeRegime = 'IDLE' | 'VALUE' | 'PULL' | 'PAIR' | 'HOLD' | 'PAUSED' | 'STOPPED';
export type PenelopeLiquidityGatePolicy = 'pause' | 'hold' | 'quote';
/** A researcher's own variant: the game's config with `overlay` applied, run beside the baseline in simulation. */
export interface PenelopeVariantSpec { name: string; overlay: Record<string, unknown>; legs?: 'noNo' | 'yesYes' | 'oneMarket' }
/** A built-in variant by name, or a custom spec. */
export type PenelopeVariant = string | PenelopeVariantSpec;
export const isVariantSpec = (v: PenelopeVariant): v is PenelopeVariantSpec => typeof v === 'object' && v !== null;
export const variantName = (v: PenelopeVariant): string => (isVariantSpec(v) ? v.name : v);

/** The knobs the control edits. The relay holds more (see PenelopeGameConfig); unknown keys ride along untouched. */
export interface PenelopeConfig {
  enabled: boolean;
  mode: 'shadow' | 'live';
  lean: PenelopeLean;
  clipContracts: number;
  maxNetContracts: number;
  leanMaxNetContracts: number;
  maxGrossContracts: number;
  minEdgeCents: number;
  cancelEdgeCents: number;
  pairEnabled: boolean;
  pairLockCents: number;
  pullPolicy: 'cancelAll' | 'cancelAdding' | 'hold' | 'widen';
  /** What may take a resting order off (relay default stay); pullPolicy applies under chase only. */
  restPolicy?: 'stay' | 'chase';
  restEdgeMaxAgeMs?: number;
  liquidityGatePolicy?: PenelopeLiquidityGatePolicy;
  variants: PenelopeVariant[];
  [k: string]: unknown;
}

interface Holding { contracts: number; costCents: number }
export interface PenelopeLive {
  /** false = the baseline is placing real orders on Kalshi (the named variants stay simulated). */
  shadow: boolean;
  gameKey: string | null;
  phase: { phase: 'pre' | 'live' | 'final' | 'unknown'; reason: string | null };
  baseline: {
    regime: PenelopeRegime;
    gate: string | null;
    stopReason: string | null;
    /** Pinnacle fair per team in cents; A = ticker A's YES team (the away side as attached here). */
    fairC: { A: number | null; B: number | null };
    pinnacleState: 'present' | 'absent' | 'withdrawn' | null;
    fairBlock: string | null;
    vendorAgeMs: number | null;
    inv: { A: Holding; B: Holding };
    net: number;
    pnlCents: number;
    rests: { A: { px: number; size: number } | null; B: { px: number; size: number } | null };
    places: number;
    cancels: number;
  };
  variants: Array<{ name: string; regime: PenelopeRegime; net: number; pnlCents: number }>;
}

export interface PenelopeGame {
  id: string;
  eventTicker: string;
  tickerA: string;
  tickerB: string;
  status: string;
  stopReason: string | null;
  config: PenelopeConfig;
  fillsCount: number;
  live: PenelopeLive | null;
  /** Armed but not running, and why. */
  parked: string | null;
}

export interface PenelopeControl {
  game: PenelopeGame | null;
  /** A game can be attached here (supported series, and the caller holds the grant). */
  eligible: boolean;
  reasons: string[];
  /** The relay service is up (PENELOPE=1). */
  serviceRunning: boolean;
  /** May a game of the caller's run LIVE here (box flag AND not locked to practice)? */
  liveEligible: boolean;
  /** Why not, in the trader's words; null when eligible. */
  liveReason: string | null;
  lastStopped: PenelopeLastStopped | null;
  /** Box and per-trader game slots, for the "N of M in use" line; null until read. */
  slots: { running: number; maxArmed: number; mine: number; maxMine: number } | null;
  error: string | null;
  busy: boolean;
  /** Attach disarmed, with `config` as the starting settings (else the relay's defaults). */
  attach: (config?: Partial<PenelopeConfig>) => Promise<void>;
  /** One click: attach this game if it is not attached yet (with `config` as its starting settings), then arm it. */
  run: (config?: Partial<PenelopeConfig>) => Promise<void>;
  patch: (config: Partial<PenelopeConfig>) => Promise<void>;
  setArm: (armed: boolean) => Promise<void>;
  kill: () => Promise<void>;
  remove: () => Promise<void>;
}

/** A game of the caller's that stopped on its own recently (the ticket's "Stopped: <reason>" banner). */
export interface PenelopeLastStopped { stopReason: string | null; at: string; mode: 'live' | 'shadow' }

interface ByEventResponse {
  game: PenelopeGame | null;
  eligible: boolean;
  reasons: string[];
  /** Box allows live AND the caller is not locked to practice. */
  liveEligible?: boolean;
  advancedEligible?: boolean;
  lastStopped?: PenelopeLastStopped | null;
  /** The caller's armed games on this box (against health.maxArmedPerTrader). */
  armedByCaller?: number;
  health: { running: boolean; enabled?: boolean; liveAllowed?: boolean; runners?: number; maxArmed?: number; maxArmedPerTrader?: number };
}

/** `enabled` false polls nothing: the caller has no grant, so there is nothing it may read. */
export function usePenelope(game: GameRow | null, enabled: boolean): PenelopeControl {
  const [data, setData] = useState<ByEventResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ticker A is the AWAY market and ticker B the HOME market, so "team A" is always the away side on this screen.
  const attachInput = useMemo(() => {
    const legs = game ? larryLegsOf(game) : null;
    if (!game || !legs) return null;
    return { eventTicker: eventTickerOfMarket(legs.tickerA), ...legs, labelA: game.awayCode, labelB: game.homeCode, gameKey: game.key };
  }, [game]);
  const eventTicker = attachInput?.eventTicker ?? null;

  const read = useCallback(
    async (signal: AbortSignal) => {
      if (!eventTicker) return;
      try {
        const res = await apiFetch(`/api/penelope/games/by-event/${encodeURIComponent(eventTicker)}`);
        const body = (await res.json().catch(() => ({}))) as ByEventResponse & { error?: string };
        if (signal.aborted) return;
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setData(body);
        setError(null);
      } catch (e) {
        if (!signal.aborted) setError((e as Error).message);
      }
    },
    [eventTicker],
  );
  const { refresh } = useVisibilityAwarePoll(read, POLL_MS, { enabled: enabled && eventTicker !== null });

  const call = useCallback(
    async (path: string, method: string, body?: unknown) => {
      setBusy(true);
      try {
        const res = await apiFetch(path, {
          method,
          headers: { 'content-type': 'application/json' },
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || `HTTP ${res.status}`);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [refresh],
  );

  const id = data?.game?.id ?? null;
  return {
    game: data?.game ?? null,
    eligible: data?.eligible ?? false,
    reasons: data?.reasons ?? [],
    serviceRunning: data?.health.running ?? false,
    liveEligible: data?.liveEligible === true,
    liveReason: !data || data.liveEligible === true ? null
      : data.health.liveAllowed === false ? 'live orders are off on this box'
        : 'a manager set you to practice (shadow) only',
    lastStopped: data?.lastStopped ?? null,
    slots: data && typeof data.health.maxArmed === 'number' && typeof data.health.runners === 'number'
      ? { running: data.health.runners, maxArmed: data.health.maxArmed, mine: data.armedByCaller ?? 0, maxMine: data.health.maxArmedPerTrader ?? Number.POSITIVE_INFINITY }
      : null,
    error,
    busy,
    attach: useCallback(async (config) => { if (attachInput) await call('/api/penelope/games', 'POST', config ? { ...attachInput, config } : attachInput); }, [attachInput, call]),
    run: useCallback(async (config) => {
      if (!attachInput) return;
      setBusy(true);
      try {
        const post = async (path: string, body?: unknown): Promise<{ game?: { id?: string } }> => {
          const res = await apiFetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
          const json = (await res.json().catch(() => ({}))) as { error?: string; game?: { id?: string } };
          if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
          return json;
        };
        // Attach is born disarmed on the relay; arming is its own call, so a failed arm leaves an attached, idle game.
        const gameId = id ?? (await post('/api/penelope/games', config ? { ...attachInput, config } : attachInput)).game?.id ?? null;
        if (gameId) await post(`/api/penelope/games/${gameId}/arm`);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
        await refresh();
      }
    }, [attachInput, id, refresh]),
    patch: useCallback(async (config) => { if (id) await call(`/api/penelope/games/${id}`, 'PATCH', { config }); }, [id, call]),
    setArm: useCallback(async (armed) => { if (id) await call(`/api/penelope/games/${id}/${armed ? 'arm' : 'disarm'}`, 'POST'); }, [id, call]),
    kill: useCallback(async () => { if (id) await call(`/api/penelope/games/${id}/kill`, 'POST', { confirm: true }); }, [id, call]),
    remove: useCallback(async () => { if (id) await call(`/api/penelope/games/${id}`, 'DELETE'); }, [id, call]),
  };
}
